// Shared exact source tokenizer. Browser use is advisory; server scope and QA remain authoritative.
const VOID = new Set('area base br col embed hr img input link meta param source track wbr'.split(' '));
export class ScopeRefusal extends Error {
  constructor(code, status, detail = {}) { super(detail.reason || code); Object.assign(this, { code, status, detail }); }
}
const refuse = (code, status, detail) => { throw new ScopeRefusal(code, status, detail); };
export function decode(s) {
  return s.replace(/&#(x[0-9a-f]+|[0-9]+);?/gi, (_, n) => {
    const cp = n[0].toLowerCase() === 'x' ? parseInt(n.slice(1), 16) : Number(n);
    return cp > 0 && cp <= 0x10ffff ? String.fromCodePoint(cp) : '\ufffd';
  }).replace(/&(amp|quot|apos|lt|gt|colon|tab|newline);/gi, (_, n) => ({ amp: '&', quot: '"', apos: "'", lt: '<', gt: '>', colon: ':', tab: '\t', newline: '\n' })[n.toLowerCase()]);
}
export function attributes(tag) {
  const a = [];
  const rx = /([^\s=<>/]+)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/g;
  for (const m of tag.matchAll(rx)) a.push({ name: m[1].toLowerCase(), value: decode(m[2] ?? m[3] ?? m[4]) });
  return a;
}
// Comments and conditional branches are tokens, not an HTML regex replacement.
// Limits cap both work and nesting. Raw CSS is consumed as text until </style>.
// The elements HTML defines (living standard + the obsolete ones real emails still use). A name outside this
// set has no HTML void/non-void rule, so its author's self-closing slash is honored (TE-112).
const HTML_ELEMENTS = new Set(('a abbr acronym address applet area article aside audio b base basefont bdi bdo bgsound big blink blockquote body br button canvas caption center cite code col colgroup data datalist dd del details dfn dialog dir div dl dt em embed fieldset figcaption figure font footer form frame frameset h1 h2 h3 h4 h5 h6 head header hgroup hr html i iframe image img input ins isindex kbd keygen label legend li link listing main map mark marquee menu menuitem meta meter multicol nav nextid nobr noembed noframes noscript object ol optgroup option output p param picture plaintext pre progress q rb rp rt rtc ruby s samp script search section select slot small source spacer span strike strong style sub summary sup table tbody td template textarea tfoot th thead time title tr track tt u ul var video wbr xmp').split(' '));
// TE-120 (🌑 sprint review 09-14, P1): a hand-rolled start/end-tag scanner with the SAME acceptance set
// as the former regex `/^<(\/?)([A-Za-z][\w:-]*)(?:\s+(?:[^<>"']|"[^"]*"|'[^']*')*)?\s*(\/?)>/` (the
// differential tooth over the shipped corpus proves it), in ONE forward pass: no backtracking, so an
// unterminated tag costs O(length) instead of O(length³) — one 4 KB propose body wedged the single pm2
// process for ~20 s. Returns { end, close, name } (end = index after `>`) or null when no tag matches.
const NAME_START = /[A-Za-z]/, NAME_CHAR = /[\w:-]/, WS = /\s/;
export function startTag(html, i) {
  let p = i + 1; const close = html[p] === '/'; if (close) p++;
  if (!NAME_START.test(html[p] || '')) return null;
  let q = p + 1; while (q < html.length && NAME_CHAR.test(html[q])) q++;
  const name = html.slice(p, q); p = q;
  if (WS.test(html[p] || '')) {                            // the attribute run: anything but an unquoted < or >; a quoted value is consumed whole
    for (; p < html.length; p++) {
      const c = html[p];
      if (c === '"' || c === "'") { const e = html.indexOf(c, p + 1); if (e < 0) return null; p = e; continue; }
      if (c === '<') return null;
      if (c === '>') return { end: p + 1, close, name };
    }
    return null;
  }
  if (html[p] === '/') p++;                                 // no whitespace after the name: only an optional slash, then >
  return html[p] === '>' ? { end: p + 1, close, name } : null;
}
// TE-120: the wall-clock budget is a LAST line (the scanner above is linear, the size cap is 900 KB);
// a tokenizer that ever gets slow again refuses loudly by name instead of wedging the process.
export const TOKENIZE_BUDGET_MS = 2000;
// One shared ceiling for both documents and every selected member. Repeated
// regions do not each buy another tokenizer budget. No request may tune this.
export function createScopeCheckpoint() {
  const deadline = performance.now() + TOKENIZE_BUDGET_MS;
  return () => {
    const remaining = deadline - performance.now();
    if (remaining <= 0) refuse('scope_escape', 422, { reason: 'time_budget' });
    return remaining;
  };
}
export function tokenize(html, { budgetMs = TOKENIZE_BUDGET_MS } = {}) {
  if (html.length > 900000) refuse('scope_escape', 422, { reason: 'unbalanced' });
  const tokens = [], foreign = []; let i = 0; const t0 = performance.now();
  const push = (end, kind, extra = {}) => { tokens.push({ start: i, end, raw: html.slice(i, end), kind, ...extra }); i = end; };
  while (i < html.length) {
    if (tokens.length > 150000) refuse('scope_escape', 422, { reason: 'unbalanced' });
    if (performance.now() - t0 > budgetMs) refuse('scope_escape', 422, { reason: 'time_budget' });
    const rest = html.slice(i);
    let m;
    if ((m = /^<!--\[if\s[^\]]+\]>(?:<!-->)?/i.exec(rest))) { push(i + m[0].length, 'if'); continue; }
    if ((m = /^(?:<!--)?<!\[endif\]-->/i.exec(rest))) { push(i + m[0].length, 'endif'); continue; }
    if (rest.startsWith('<!--')) {
      const end = html.indexOf('-->', i + 4);
      if (end < 0 || /^<!--\s*\[if/i.test(rest)) refuse('scope_escape', 422, { reason: 'unbalanced' });
      push(end + 3, 'comment'); continue;
    }
    if ((m = /^<!doctype\s[^>]*>/i.exec(rest))) { push(i + m[0].length, 'doctype'); continue; }
    if (html[i] === '<') {
      const tag = startTag(html, i);
      if (!tag) refuse('scope_escape', 422, { reason: 'unbalanced' });
      const text = html.slice(i, tag.end), name = tag.name.toLowerCase(), close = tag.close;
      // TE-112 (26 shipped Synchrony/Sabre files as the fixture set, TE-70): the self-closing slash closes the
      // element for SVG/MathML foreign content (as before) and for any element name HTML does not define —
      // namespaced Office/VML elements (`<v:fill/>` in an MSO branch) and the ESP tracking tag
      // `<custom name="opencounter" type="tracking" />` that closes every Synchrony email — because no HTML
      // parsing rule exists to violate. HTML's own non-void elements keep AMEND-3: `<div/>` still opens.
      // The Outlook `<xml>` island itself is consumed as raw text below (like <style>), never as a tag stack.
      const foreignRoot = name === 'svg' || name === 'math', selfClosing = /\/\s*>$/.test(text);
      const isVoid = VOID.has(name) || ((foreign.length > 0 || foreignRoot || !HTML_ELEMENTS.has(name)) && selfClosing);
      push(tag.end, 'tag', { name, close, void: isVoid, attrs: attributes(text) });
      if (foreignRoot) {
        if (close && foreign.at(-1) === name) foreign.pop();
        else if (!close && !isVoid) foreign.push(name);
      }
      // TE-112: an Outlook `<xml>` island is opaque to every browser and read only by Word's engine; two shipped
      // Synchrony files carry a doubled `</o:OfficeDocumentSettings>` inside it. Scope machinery must not depend on
      // the island's internal well-formedness — consume it as raw text like <style>; the scanner still sees the bytes.
      if (!close && (name === 'style' || name === 'script' || name === 'xml')) {
        const endRx = new RegExp(`</${name}\\s*>`, 'ig'); endRx.lastIndex = i;
        const end = endRx.exec(html);
        if (!end) refuse('scope_escape', 422, { reason: 'unbalanced' });
        if (end.index > i) push(end.index, 'text');
      }
      continue;
    }
    const end = html.indexOf('<', i); push(end < 0 ? html.length : end, 'text');
  }
  return tokens;
}
